import { useMediaQueryWithMount } from "@/lib/hooks/ui/use-media-query";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { useColumnManager } from "@/lib/column-manager";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { useAuthenticatedUser } from "@/lib/hooks/auth/use-authenticated-user";
import { useRef } from "react";

/**
 * Stable fallbacks, hoisted to module scope on purpose.
 *
 * Everything this hook returns is fed to `useSimpleTableContextValue` as a
 * MEMBER-LEVEL dependency (`setup.appUser`, `contextVisibleColumns`,
 * `contextToggleColumn`, …). A fallback allocated inline is a fresh reference
 * on EVERY render, so the memo it keys can never hold — and the stable context
 * slice is what `MemoizedTableRow` / `MemoizedTableCell` rely on, so one
 * unstable member repaints ~35 virtualized rows x N cells per render. See
 * MEMO_STABLE_PROPS and the header of `SimpleTableContextValue/use-context-value.ts`.
 */
const ANONYMOUS_APP_USER = { id: "", email: "", role: "client_user" as const };
const EMPTY_COLUMN_LIST: never[] = [];
const NOOP = (): void => {};

/**
 * Hook that contains ALL hook calls for SimpleTable.
 * CRITICAL: All hooks must be called unconditionally in the same order every render.
 * This fixes the "rendered fewer hooks than expected" error.
 */
export function useSimpleTableHooks() {
  // ============================================================================
  // ALL HOOKS MUST BE CALLED HERE - BEFORE ANY CONDITIONAL LOGIC
  // ============================================================================

  // Mobile detection (SSR-safe). `hasMounted` lets the view layer hold a
  // neutral state until the real viewport is known, preventing a
  // desktop-table-then-mobile-cards flash on first paint.
  const { matches: isMobile, mounted: hasMounted } = useMediaQueryWithMount(
    "(max-width: 768px)"
  );

  // Auth context - Get current user for resolving "me" values.
  // `useAuthenticatedUser` memoizes the adapted user: `setup.appUser` is a
  // dependency of the `stable` context slice, so a fresh reference per render
  // would stop that slice memoizing (see use-authenticated-user.ts).
  const { appUser } = useAuthenticatedUser();

  // Field filtering - Role-based field filtering
  const { filterFieldsByRole, roleName } = useFieldFiltering();

  // Column manager - CRITICAL FIX: Always call unconditionally
  // If provider is missing, React will throw a clear error for debugging
  const columnManager = useColumnManager();

  // Global modal stack - CRITICAL FIX: Must be called BEFORE any early returns
  // Previously this was called after the mobile early return, causing hook ordering issues
  const { openModal } = useGlobalModalStack();

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const tableHeaderScrollRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);

  // Extract column manager values with fallback
  const contextVisibleColumns = columnManager?.visibleColumns || EMPTY_COLUMN_LIST;
  const contextToggleColumn = columnManager?.toggleColumn || NOOP;
  const hiddenColumns = columnManager?.hiddenColumns || EMPTY_COLUMN_LIST;
  const contextColumnOrder = columnManager?.columnOrder || EMPTY_COLUMN_LIST;
  const contextSetColumnOrder = columnManager?.setColumnOrder || NOOP;
  // Per-table prefs key (shared with visibility/order) so width + page size
  // persist to the same per-user, per-table namespace in user_prefs.
  const tablePrefsKey = columnManager?.storageKey;

  return {
    isMobile,
    hasMounted,
    appUser: appUser || ANONYMOUS_APP_USER, // Fallback for null case
    filterFieldsByRole,
    roleName,
    contextVisibleColumns,
    contextToggleColumn,
    hiddenColumns,
    contextColumnOrder,
    contextSetColumnOrder,
    tablePrefsKey,
    openModal,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
  };
}
